import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ScatterController,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Stack, Typography } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { useAppSelector } from '../../../../../hooks';
import { getSubmissionsV2State } from '../../../../../store/V2/SubmissionsSlice';
import { TP_COLORS } from '../../../../../constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ScatterController
);

const createCustomRect = () => {
  const width = 6;
  const height = 18;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  canvas.width = width;
  canvas.height = height;

  ctx.fillStyle = TP_COLORS.gray.darken;
  ctx.fillRect(0, 0, width, height);

  const img = new Image();
  img.src = canvas.toDataURL();
  return img;
};

const initialData: any = {
  labels: [] as string[],
  datasets: [
    {
      label: 'Score',
      data: [] as number[],
      backgroundColor: '#14466D',
      borderRadius: 5,
    },
  ],
};

const MarkedBarChart: React.FC = () => {
  // State
  const [data, setData] = useState(initialData);

  // Store
  const { courseSubmissionsData, studentSubmissionsData } = useAppSelector(
    getSubmissionsV2State
  );

  useEffect(() => {
    if (
      courseSubmissionsData?.groupedSubmissions &&
      studentSubmissionsData?.groupedSubmissions
    ) {
      const labels: string[] = Array.from(
        new Set(
          courseSubmissionsData.groupedSubmissions.map(
            (submission) => submission.topicName
          )
        )
      );

      const averageScores = courseSubmissionsData.groupedSubmissions.map(
        (submission) => submission.averageScore
      );

      const averageScoresData = labels.map((_, i) => ({
        x: averageScores[i],
        y: i,
      }));

      const studentScores: number[] = [];

      labels.forEach((label) => {
        const value = studentSubmissionsData.groupedSubmissions.find(
          (submission) => submission.topicName === label
        );

        studentScores.push(value?.averageScore || 0);
      });

      const getScoreBgColors = (context) => {
        if (
          averageScoresData[context.dataIndex].x ===
          studentScores[context.dataIndex]
        ) {
          return TP_COLORS.yellow.light;
        }

        if (
          averageScoresData[context.dataIndex].x <
          studentScores[context.dataIndex]
        ) {
          return TP_COLORS.green.light;
        }

        return TP_COLORS.orange.light;
      };

      const getScatterBgColors = (context) => {
        if (
          averageScoresData[context.dataIndex].x >
            studentScores[context.dataIndex] &&
          studentScores[context.dataIndex] > 0
        ) {
          return `${TP_COLORS.orange.light}1a`;
        }

        return '#F1F5F9';
      };

      const getForamtedAverageData = () => {
        return averageScoresData.map((item, index) => {
          if (item.x < studentScores[index]) {
            return 0;
          }

          if (item.x === studentScores[index]) {
            return 0;
          }

          if (item.x > studentScores[index]) {
            return item.x - studentScores[index];
          }

          return item.x;
        });
      };

      setData({
        labels,
        datasets: [
          {
            label: 'Class Average',
            type: 'scatter',
            pointBackgroundColor: 'black',
            pointBorderColor: 'black',
            pointRadius: 10,
            pointHoverRadius: 8,
            pointBorderWidth: 2,
            data: averageScoresData,
            pointStyle: createCustomRect(),
          },
          {
            label: 'Student Score',
            stack: 'Stack 0',
            borderRadius: 3,
            barThickness: 18,
            data: studentScores,
            backgroundColor: getScoreBgColors,
          },
          {
            label: 'Class Average',
            borderRadius: 3,
            barThickness: 18,
            stack: 'Stack 0',
            data: getForamtedAverageData(),
            backgroundColor: getScatterBgColors,
          },
        ],
      });
    }
  }, [courseSubmissionsData, studentSubmissionsData]);

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (tooltipItem) => {
            const { dataset, formattedValue } = tooltipItem;
            return `${dataset.label}: ${formattedValue}/20`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 20,
        title: {
          display: true,
          text: 'Score',
        },
      },
      y: { beginAtZero: true },
    },
  };

  return (
    <Stack spacing={4} marginTop={2}>
      <Box>
        <Typography fontSize="18px" color={TP_COLORS.gray.darken}>
          Accuracy by Topic
        </Typography>
        <Typography fontSize="14px" color="#647488">
          Comparison of Student Scores to Class Average
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <CircleIcon sx={{ color: TP_COLORS.green.light, fontSize: 12 }} />
          <Typography variant="body2">Above Average</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircleIcon sx={{ color: TP_COLORS.yellow.light, fontSize: 12 }} />
          <Typography variant="body2">Average</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircleIcon sx={{ color: TP_COLORS.orange.light, fontSize: 12 }} />
          <Typography variant="body2">Below Average</Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          width: '100%',
          minHeight: '400px',
          height: `${50 * (data.labels?.length || 10)}px`,
        }}
      >
        <Bar data={data} options={options} />
      </Box>
    </Stack>
  );
};

export default MarkedBarChart;
